/**
 * `createThemedStyles` — themed StyleSheet hook factory.
 *
 * The makeStyles pattern: the style factory is captured once at module scope,
 * and the returned `useStyles` hook resolves tokens via `useTheme()` and
 * memoizes `StyleSheet.create(factory(tokens))` keyed on the theme alone —
 * sheets stay referentially stable across re-renders and invalidate exactly
 * when the active theme changes. Lives in its own module so `tokens.ts` and
 * `context.tsx` stay free of the `react-native` dependency.
 */

import { useMemo } from 'react';
import { StyleSheet } from 'react-native';

import { useTheme } from './context';
import type { Theme } from './tokens';

/**
 * Turns a tokens-to-styles factory into a `useStyles` hook. Call at module
 * scope, exactly where `StyleSheet.create` sits today:
 *
 * ```ts
 * const useStyles = createThemedStyles(tokens => ({
 *     card: { backgroundColor: tokens.colors.colorSurfacePrimary },
 * }));
 * ```
 * @public
 */
export function createThemedStyles<T extends StyleSheet.NamedStyles<T>>(factory: (tokens: Theme) => T): () => T {
    return function useStyles(): T {
        const tokens = useTheme();
        return useMemo(() => StyleSheet.create(factory(tokens)), [tokens]);
    };
}
